// frontend/src/components/detail/tables/JobsTable.jsx
import React from 'react';
import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DataTable } from '../../tablekit';
import { F } from '../../../utils/qualFmt';
import { slugify } from '../../../utils/slug';

/* -------------------- width map (edit here) -------------------- */
const W = {
  parent: {
    name: { minWidth: 260 },         // Job
    count: { width: 120, align: 'right' },       // Occurrences
    pct: { width: 100, align: 'right' },         // Share
    avgProductSat: { minWidth: 240, align: 'right' }, // Avg product satisfaction (qual text)
    open: { width: 64, align: 'center' },        // Open
  },
  child: {
    name: { width: 100 },               // Name
    date: { width: 100 },               // Date
    segmentation: { minWidth: 200 },    // Segmentation
    chosenSolution: { minWidth: 140 },  // Chosen Solution
    importance: { width: 140, align: 'right' },  // Importance
    satisfaction: { minWidth: 180, align: 'right' }, // Satisfaction (qual text)
    summary: { minWidth: 400 },         // Summary
    open: { width: 64, align: 'center' } // Open
  }
};

/* -------------------- helpers -------------------- */

function formatSegmentation(seg) {
  if (!seg || typeof seg !== 'object') return '—';
  const parts = [];

  Object.entries(seg).forEach(([k, v]) => {
    if (v == null) return;
    if (Array.isArray(v)) {
      const list = v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
      parts.push(`${k}: ${list}`);
    } else if (typeof v === 'object') {
      const inner = Object.entries(v)
        .map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`)
        .join('; ');
      parts.push(`${k}: { ${inner} }`);
    } else {
      parts.push(`${k}: ${String(v)}`);
    }
  });

  return parts.length ? parts.join(' • ') : '—';
}

/** Build aggregated rows from raw customer stories if `rows` prop isn't provided.
 * Shape produced:
 * {
 *   key, name, count, pct, avgProductSat, href,
 *   instances: [{ name, date, segmentation, chosenSolution, importance, satisfaction, summary, href }]
 * }
 */
function buildFromStories(customerStories = []) {
  const groups = new Map(); // jobName → { name, instances: [] }
  let totalOccurrences = 0;

  for (const s of customerStories) {
    const jobs = Array.isArray(s?.jobs_to_be_done) ? s.jobs_to_be_done : [];
    for (const j of jobs) {
      const name = j?.job_to_be_done_category || 'Uncategorized';
      if (!groups.has(name)) groups.set(name, { name, instances: [] });

      // Try to pull job-level fields if present; fall back to story-level when sensible
      const importance = Number.isFinite(j?.importance) ? j.importance : null;
      // satisfaction can come from job or product-level; keep null if not available
      const satisfaction =
        Number.isFinite(j?.satisfaction_level) ? j.satisfaction_level :
        Number.isFinite(s?.satisfaction_level) ? s.satisfaction_level : null;

      groups.get(name).instances.push({
        name: s?.customer_name || 'Interview',
        date: s?.conversation_date || '',
        segmentation: s?.segmentation_data || null,
        chosenSolution: s?.solution_choice || s?.solution_choice_category || '',
        importance,
        satisfaction,
        // Summary should show the satisfaction justification
        summary:
          s?.satisfaction_justification ??
          s?.satisfaction_level_justification ??
          j?.satisfaction_justification ??
          j?.satisfaction_level_justification ??
          j?.job_to_be_done_summary ??
          j?.summary ??
          s?.summary ??
          '',
        
        href: `/conversations?search=${encodeURIComponent(s?.customer_name || '')}`
      });

      totalOccurrences += 1;
    }
  }

  const rows = [];
  for (const [jobName, data] of groups.entries()) {
    const count = data.instances.length;
    const avgProductSat = (() => {
      const vals = data.instances.map(i => Number.isFinite(i.satisfaction) ? i.satisfaction : null).filter(v => v != null);
      if (!vals.length) return null;
      return vals.reduce((a, b) => a + b, 0) / vals.length;
    })();

    rows.push({
      key: slugify(jobName),
      name: jobName,
      count,
      pct: totalOccurrences > 0 ? ((count / totalOccurrences) * 100) : 0,
      avgProductSat,
      href: `/jobs/${slugify(jobName)}`,
      instances: data.instances.sort((a, b) => new Date(b.date) - new Date(a.date))
    });
  }

  // default sort: count desc (same behavior as before)
  rows.sort((a, b) => b.count - a.count);
  return rows;
}

/* Render a small pill for percentage "xx%" */
const Pct = (v) => (
  <Chip size="small" label={`${(Number(v) || 0).toFixed(1)}%`} />
);

/* -------------------- component -------------------- */

export default function JobsTable({
  /** Backwards-compat: if `rows` is provided, we use it as-is (DashboardNew keeps working). */
  rows,
  /** New: allow building the table directly from stories (CurrentSolutions). */
  customerStories = [],
  /** Optional custom title */
  title = 'Jobs'
}) {
  const tableRows = React.useMemo(
    () => (Array.isArray(rows) && rows.length ? rows : buildFromStories(customerStories)),
    [rows, customerStories]
  );

  if (!tableRows.length) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No jobs found.</Typography>;
  }

  // ----- parent columns -----
  const columns = [
    { key: 'name', header: 'Job', ...W.parent.name },
    { key: 'count', header: 'Occurrences', type: 'number', align: 'right', ...W.parent.count },
    {
      key: 'pct',
      header: 'Share',
      type: 'number',
      align: 'right',
      accessor: (r) => r.pct,
      render: (v) => Pct(v),
      ...W.parent.pct
    },
    {
      key: 'avgProductSat',
      header: 'Avg product satisfaction',
      type: 'number',
      align: 'right',
      accessor: (r) => (Number.isFinite(r.avgProductSat) ? r.avgProductSat : null),
      render: (v) => (v != null ? F.productSatisfaction(v).text : '—'),
      ...W.parent.avgProductSat
    },
    {
      key: 'href',
      header: 'Open',
      accessor: (r) => r.href || '',
      align: 'center',
      render: (href) =>
        href ? (
          <Tooltip title="Open job">
            <IconButton size="small" component="a" href={href} aria-label="Open job">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : '—',
      ...W.parent.open
    }
  ];

  // ----- sub table spec -----
  const subTables = [
    {
      key: 'instances',
      title: 'Interviews',
      columns: [
        { key: 'name', header: 'Name', ...W.child.name },
        { key: 'date', header: 'Date', type: 'date', ...W.child.date },
        {
          key: 'segmentation',
          header: 'Segmentation',
          accessor: (r) => formatSegmentation(r.segmentation),
          ...W.child.segmentation
        },
        { key: 'chosenSolution', header: 'Chosen Solution', ...W.child.chosenSolution },
        { key: 'importance', header: 'Importance', type: 'number', align: 'right', ...W.child.importance },
        {
          key: 'satisfaction',
          header: 'Satisfaction',
          type: 'number',
          align: 'right',
          accessor: (r) => (Number.isFinite(r.satisfaction) ? r.satisfaction : null),
          render: (v) => (v != null ? F.productSatisfaction(v).text : '—'),
          ...W.child.satisfaction
        },
        { key: 'summary', header: 'Summary', ...W.child.summary },
        {
          key: 'href',
          header: 'Open',
          accessor: (r) => r.href || '',
          render: (href) =>
            href ? (
              <Tooltip title="Open interview">
                <IconButton
                  size="small"
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener"
                  aria-label="Open interview"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : '—',
          ...W.child.open
        }
      ]
    }
  ];

  return (
    <DataTable
      title={title}
      columns={columns}
      rows={tableRows}
      subTables={subTables}
      dense
    />
  );
}

